"use client";

/**
 * Channel-Talk-style landing screen — first thing a user sees when opening the
 * support widget. Greets the user and surfaces the primary "ask us" CTA along
 * with the technical-only scope notice.
 */

import Image from "next/image";
import { useTranslations } from "next-intl";
import { Send } from "lucide-react";
import { renderEmphasis } from "./renderEmphasis";

interface SupportHomeTabProps {
  displayName: string | null;
  onStartChat: () => void;
}

export function SupportHomeTab({
  displayName,
  onStartChat,
}: SupportHomeTabProps) {
  const t = useTranslations("support.home");
  const tChat = useTranslations("support.chat");

  const greeting = displayName
    ? t("greetingNamed", { name: displayName })
    : t("greetingAnonymous");

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-y-auto bg-gray-50 px-3 py-4">
      <div className="rounded-2xl bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.08)]">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white border border-gray-400/30">
            <Image
              src="/locarora_sq.png"
              alt=""
              width={36}
              height={36}
              className="h-10 w-10 object-cover"
              // 로컬 /public 자산 — 정적 CDN 원본 서빙(Vercel 이미지 최적화 과금 제외).
              unoptimized
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-semibold text-gray-900">LOCARORA</p>
            <p className="mt-1 whitespace-pre-line text-[14px] leading-relaxed text-gray-700">
              {greeting}
              {"\n"}
              {t("subtitle")}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onStartChat}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-orange-600 active:bg-orange-700"
        >
          {t("ctaContact")}
          <Send className="h-3.5 w-3.5" />
        </button>

        <p className="mt-3 flex items-center justify-center gap-1.5 text-[12px] text-gray-500">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
          {t("statusFast")}
        </p>
      </div>

      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3.5">
        <p className="text-[14px] font-semibold text-red-800">
          {tChat("pinnedTitle")}
        </p>
        <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-[13px] leading-relaxed text-red-700 marker:text-red-400">
          {tChat("pinnedBody")
            .split("\n")
            .map((line, idx) => (
              <li key={idx}>{renderEmphasis(line)}</li>
            ))}
        </ul>
      </div>
    </div>
  );
}
